import { useMemo, useState } from 'react';
import { Input, Radio, List, Typography, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { TranslationFilter } from '@/types/translation';

const { Text } = Typography;

interface Props {
  allKeys: string[]; // union of source + target
  sourceValues: Record<string, string>;
  targetValues: Record<string, string>;
  selectedKey: string | null;
  onSelect: (key: string) => void;
}

export function KeyListColumn({
  allKeys,
  sourceValues,
  targetValues,
  selectedKey,
  onSelect,
}: Props) {
  const [filter, setFilter] = useState<TranslationFilter>('all');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const lq = q.toLowerCase();
    return allKeys
      .filter((k) => {
        if (lq && !k.toLowerCase().includes(lq)) return false;
        const hasTarget = (targetValues[k] ?? '').trim() !== '';
        switch (filter) {
          case 'missing':
            return !hasTarget;
          case 'ai-generated':
            return hasTarget; // server doesn't yet tag source; both buckets approximated
          case 'manually-edited':
            return hasTarget;
          case 'all':
            return true;
        }
      })
      .sort();
  }, [allKeys, targetValues, filter, q]);

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Input
        prefix={<SearchOutlined />}
        placeholder="Filter keys"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        allowClear
      />
      <Radio.Group
        size="small"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        optionType="button"
        buttonStyle="solid"
        options={[
          { value: 'all', label: 'All' },
          { value: 'missing', label: 'Missing' },
        ]}
        style={{ width: '100%' }}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {filtered.length} keys
      </Text>
      <List
        size="small"
        style={{
          maxHeight: 'calc(100vh - 280px)',
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
        }}
        dataSource={filtered}
        renderItem={(key) => {
          const hasSrc = (sourceValues[key] ?? '').trim() !== '';
          const hasTgt = (targetValues[key] ?? '').trim() !== '';
          return (
            <List.Item
              onClick={() => onSelect(key)}
              style={{
                padding: '6px 12px',
                cursor: 'pointer',
                background: key === selectedKey ? '#e6f4ff' : undefined,
              }}
            >
              <span style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}>{key}</span>
              <span style={{ marginLeft: 8, fontSize: 11, color: '#999' }}>
                {hasSrc ? '●' : '○'}
                {hasTgt ? '●' : '○'}
              </span>
            </List.Item>
          );
        }}
      />
    </Space>
  );
}
